import { useState } from "react";
import { formatDate, formatINR, Badge, sourceMeta } from "../../../Component/Workshop/UI/index";

// ─── helpers ──────────────────────────────────────────────────────────────────

function humanise(str = "") {
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function isImageUrl(url = "") {
  return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);
}

function isPdfUrl(url = "") {
  return /\.pdf(\?.*)?$/i.test(url);
}

const DOC_META = {
  "RC":                        { icon: "🪪", color: "#3b82f6" },
  "RC book":                   { icon: "🪪", color: "#3b82f6" },
  "Insurance Policy":          { icon: "📋", color: "#22c55e" },
  "Insurance policy":          { icon: "📋", color: "#22c55e" },
  "Driver License":            { icon: "🪪", color: "#a855f7" },
  "Driver license":            { icon: "🪪", color: "#a855f7" },
  "Fitness Certificate":       { icon: "🩺", color: "#14b8a6" },
  "Fitness certificate":       { icon: "🩺", color: "#14b8a6" },
  "Permit":                    { icon: "📄", color: "#f97316" },
  "FIR Document":              { icon: "🚨", color: "#ef4444" },
  "FIR document":              { icon: "🚨", color: "#ef4444" },
  "LR Document":               { icon: "📦", color: "#64748b" },
  "Accident Photo - Front":    { icon: "📸", color: "#eab308" },
  "Accident Photo - Rear":     { icon: "📸", color: "#eab308" },
  "Accident Photo - Left":     { icon: "📸", color: "#eab308" },
  "Accident Photo - Right":    { icon: "📸", color: "#eab308" },
  "Accident Photo - Top":      { icon: "📸", color: "#eab308" },
  "Accident Photo - Interior": { icon: "📸", color: "#eab308" },
  "Accident - front":          { icon: "📸", color: "#eab308" },
  "Accident - rear":           { icon: "📸", color: "#eab308" },
};
const docMeta = (type) => DOC_META[type] || { icon: "📁", color: "#64748b" };

function statusBorderColor(status) {
  if (!status) return "var(--wd-border)";
  if (["APPROVED", "VERIFIED"].includes(status)) return "#22c55e";
  if (status === "REJECTED") return "#ef4444";
  if (status === "PENDING") return "#eab308";
  return "var(--wd-border)";
}

function statusBadgeVariant(status) {
  if (!status) return "gray";
  if (["APPROVED", "VERIFIED"].includes(status)) return "green";
  if (status === "REJECTED") return "red";
  if (status === "PENDING") return "yellow";
  return "gray";
}

function expiryInfo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0)   return { label: "Expired",       color: "var(--wd-red)" };
  if (diff < 30)  return { label: "Expiring soon", color: "var(--wd-orange)" };
  return null;
}

// ─── sub-components ───────────────────────────────────────────────────────────

function SectionCard({ title, icon, children, action }) {
  return (
    <div style={{
      background: "var(--wd-surface)",
      border: "1px solid var(--wd-border)",
      borderRadius: 12,
      padding: "14px 16px",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12, paddingBottom: 10,
        borderBottom: "1px solid var(--wd-border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-t1)", textTransform: "uppercase", letterSpacing: ".04em" }}>
            {title}
          </span>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function FieldGrid({ fields }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
      {fields.map(({ label, value, mono, color, badge }) => (
        <div key={label} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontSize: 10, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600 }}>
            {label}
          </span>
          {badge ? (
            <span style={{
              display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 600,
              padding: "2px 8px", borderRadius: 20, width: "fit-content",
              background: `${badge.color}18`, color: badge.color,
              border: `1px solid ${badge.color}33`,
            }}>
              {value || "—"}
            </span>
          ) : (
            <span style={{
              fontSize: 12, fontWeight: 500,
              color: color || (value ? "var(--wd-t1)" : "var(--wd-t3)"),
              fontFamily: mono ? "var(--wd-mono)" : "var(--wd-sans)",
              fontStyle: value ? "normal" : "italic",
            }}>
              {value || "—"}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Document card with actual inline viewer ──────────────────────────────────

function DocCard({ doc }) {
  const [imgError, setImgError] = useState(false);
  const meta    = docMeta(doc.type);
  const border  = statusBorderColor(doc.verificationStatus || doc.status);
  const expiry  = expiryInfo(doc.ulipExpiryDate);
  const hasFile = !!doc.fileUrl;
  const isImg   = hasFile && isImageUrl(doc.fileUrl) && !imgError;
  const isPdf   = hasFile && isPdfUrl(doc.fileUrl);
  const ulipOk  = doc.ulipStatus && ["VALID", "EXPIRING_SOON"].includes(doc.ulipStatus);
  const docName = humanise(doc.type || "");

  return (
    <div style={{
      borderRadius: 10,
      border: `1.5px solid ${border}`,
      background: "var(--wd-surface)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}>

      {/* ── file preview area ── */}
      <div style={{
        height: 110,
        background: "var(--wd-hover)",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        padding: 2,
        boxSizing: "border-box",
      }}>
        {isImg ? (
          <img
            src={doc.fileUrl}
            alt={docName}
            onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : isPdf ? (
          <iframe
            src={`${doc.fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={docName}
            style={{ width: "100%", height: "130%", border: "none", pointerEvents: "none", marginTop: -20 }}
          />
        ) : (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: `${meta.color}18`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22,
            }}>
              {meta.icon}
            </div>
            <span style={{ fontSize: 10, color: "var(--wd-t3)" }}>No file uploaded</span>
          </div>
        )}

        {/* open link — top-right corner only, doesn't cover the name */}
        {hasFile && (
          <a
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            title="Open full file"
            style={{
              position: "absolute", top: 6, right: 6,
              fontSize: 10, fontWeight: 600, color: "#fff",
              background: "rgba(0,0,0,.55)", borderRadius: 5,
              padding: "2px 7px", textDecoration: "none", lineHeight: 1.8,
              backdropFilter: "none",
            }}
          >
            ↗ open
          </a>
        )}
      </div>

      {/* ── card footer — name + badges + expiry ── */}
      <div style={{ padding: "9px 10px", display: "flex", flexDirection: "column", gap: 6 }}>

        {/* doc name — full width, wraps if needed */}
        <div style={{
          fontSize: 12, fontWeight: 600, color: "var(--wd-t1)",
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}>
          {docName}
        </div>

        {/* status + ulip row */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
            background: `${border}18`, color: border,
            border: `1px solid ${border}33`,
          }}>
            {doc.verificationStatus || doc.status || "PENDING"}
          </span>
          {doc.ulipStatus ? (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
              background: ulipOk ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)",
              color: ulipOk ? "var(--wd-green)" : "var(--wd-red)",
              border: `1px solid ${ulipOk ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.25)"}`,
            }}>
              {doc.ulipStatus === "VALID" ? "ULIP valid" : doc.ulipStatus.replace(/_/g, " ")}
            </span>
          ) : (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20,
              background: "rgba(148,163,184,.08)", color: "var(--wd-t3)",
              border: "1px solid var(--wd-border)",
            }}>
              Not verified
            </span>
          )}
        </div>

        {/* expiry */}
         <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
          <span style={{ color: "var(--wd-t3)" }}>📅</span>
          {doc.ulipExpiryDate ? (
            <span style={{
              fontWeight: 600,
              color: new Date(doc.ulipExpiryDate) < new Date()
                ? "var(--wd-red)"
                : "var(--wd-green)",
            }}>
              {formatDate(doc.ulipExpiryDate)}
              {new Date(doc.ulipExpiryDate) < new Date() ? " · Expired" : ""}
            </span>
          ) : (
            <span style={{ color: "var(--wd-t3)", fontStyle: "italic" }}>No expiry data</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── alert strips ─────────────────────────────────────────────────────────────

function AlertStrip({ color, icon, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      padding: "8px 12px", borderRadius: 8, fontSize: 12,
      background: `${color}12`, color, border: `1px solid ${color}30`,
    }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export function CaseOverviewSnapshot({ detail, onClose }) {
  const { caseInfo, driverDetails, accidentDetails, insuranceDetails, documents, workshopInfo } = detail;
  const docs = documents || [];

  const approvedCount = docs.filter(d => ["APPROVED", "VERIFIED"].includes(d.verificationStatus || d.status)).length;
  const pendingDocs   = docs.filter(d => ["PENDING", null, undefined].includes(d.verificationStatus || d.status));
  const rejectedDocs  = docs.filter(d => (d.verificationStatus || d.status) === "REJECTED");
  const expiringSoon  = docs.filter(d => {
    const info = expiryInfo(d.ulipExpiryDate);
    return info && info.label === "Expiring soon";
  });

  return (
    <div
      className="wd-animate"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >

      {/* ── header bar ──────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px",
        background: "var(--wd-surface)",
        border: "1px solid var(--wd-border)",
        borderRadius: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: "rgba(99,102,241,.1)", border: "1px solid rgba(99,102,241,.2)",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
          }}>🗂</span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--wd-t1)" }}>
              Full Case Overview
            </div>
            {/* <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
              {caseInfo.accidentCaseNo} · {caseInfo.customerName} · {caseInfo.vehicleNo}
            </div> */}
          </div>
        </div>
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          onClick={onClose}
          style={{ gap: 5 }}
        >
          ✕ Close overview
        </button>
      </div>

      {/* ── row 1: vehicle + accident ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

        <SectionCard title="Vehicle & owner" icon="🚛">
          <FieldGrid fields={[
            { label: "Vehicle no.",    value: caseInfo.vehicleNo,   mono: true },
              { label: "Chassis no.",    value: caseInfo.vehicleChassisNo,  mono: true },
            { label: "Engine no.",     value: caseInfo.vehicleEngineNo, mono: true },
            { label: "Make / model",   value: accidentDetails?.vehicleMake || caseInfo.vehicleMake },
            { label: "Owner",          value: caseInfo.customerName },
            { label: "Phone",          value: caseInfo.customerPhone,  mono: true },
            { label: "Email",          value: caseInfo.customerEmail },
            { label: "Pincode",        value: caseInfo.accidentPincode,   mono: true },
          ]} />
        </SectionCard>

        <SectionCard title="Accident details" icon="📍">
          <FieldGrid fields={[
            { label: "Date",        value: formatDate(caseInfo.accidentDate) },
            { label: "Time",        value: caseInfo.accidentTime || null },
            { label: "Location",    value: caseInfo.location },
            { label: "State",       value: caseInfo.accidentState },
            { label: "FIR / GD no.", value: accidentDetails?.policeReportNo || caseInfo.policeReportNo, mono: true },
            { label: "3rd party",   value: caseInfo.thirdPartyLoss ? "Yes" : "No",
              ...(caseInfo.thirdPartyLoss ? { color: "var(--wd-red)" } : {}) },
            { label: "Source",       value: caseInfo.source ? `${sourceMeta(caseInfo.source).icon} ${sourceMeta(caseInfo.source).label}` : null,
                badge: { color: sourceMeta(caseInfo.source).color } },
          ]} />
          {accidentDetails?.description && (
            <div style={{
              marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--wd-border)",
            }}>
              <div style={{ fontSize: 10, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 600, marginBottom: 4 }}>
                Description
              </div>
              <p style={{ fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6, margin: 0 }}>
                {accidentDetails.description}
              </p>
            </div>
          )}
        </SectionCard>

      </div>

      {/* ── row 2: insurance + driver + workshop ─────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>

        <SectionCard title="Insurance" icon="🛡️">
          <FieldGrid fields={[
            { label: "Company",       value: insuranceDetails?.company || caseInfo.insuranceCompany },
            { label: "Policy type",   value: insuranceDetails?.policyType },
            { label: "Policy no.",    value: caseInfo.policyNo,                                         mono: true },
            { label: "Claim no.",     value: insuranceDetails?.claimNo,
              ...(!insuranceDetails?.claimNo ? { color: "var(--wd-orange)" } : {}) },
            { label: "Expiry",        value: formatDate(insuranceDetails?.expiryDate),
              color: insuranceDetails?.expiryDate && expiryInfo(insuranceDetails.expiryDate)
                ? expiryInfo(insuranceDetails.expiryDate).color : undefined },
            { label: "Intimation",
              value: caseInfo.intimationRaised ? "Raised" : "Not raised",
              badge: { color: caseInfo.intimationRaised ? "var(--wd-green)" : "var(--wd-orange)" } },
          ]} />
        </SectionCard>

        <SectionCard title="Driver" icon="👤">
          <FieldGrid fields={[
            { label: "Name",            value: driverDetails?.name },
            { label: "Phone",           value: driverDetails?.phone,                 mono: true },
            { label: "License no.",     value: driverDetails?.licenseNo,             mono: true },
            { label: "License expiry",  value: formatDate(driverDetails?.licenseExpiry) },
          ]} />
        </SectionCard>

        <SectionCard title="Workshop & payment" icon="🔧">
          <FieldGrid fields={[
            { label: "Workshop",         value: workshopInfo?.workshopName || caseInfo.workshopName },
            { label: "Surveyor",         value: caseInfo.surveyorName },
            { label: "Estimate",         value: caseInfo.workshopEstimateAmount ? formatINR(caseInfo.workshopEstimateAmount) : null },
            { label: "Approved amt.",    value: caseInfo.approvedAmount ? formatINR(caseInfo.approvedAmount) : null },
            { label: "SLA deadline",     value: formatDate(caseInfo.slaDeadline),
              color: caseInfo.slaStatus === "BREACHED" ? "var(--wd-red)" : undefined },
            { label: "Workshop paid",
              value: caseInfo.workshopPaymentStatus || "Not paid",
              badge: caseInfo.workshopPaymentStatus === "PAID"
                ? { color: "var(--wd-green)" }
                : { color: "var(--wd-t3)" } },
          ]} />
        </SectionCard>

      </div>

      {/* ── documents grid ────────────────────────────────────────────────────── */}
      <SectionCard
        title="Documents"
        icon="📄"
        action={
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {[
              ["#22c55e",           `${approvedCount} approved`],
              ["var(--wd-orange)",  `${pendingDocs.length} pending`],
              ["var(--wd-red)",     `${rejectedDocs.length} rejected`],
            ].map(([color, label]) => (
              <span key={label} style={{
                fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20,
                background: `${color}18`, color,
                border: `1px solid ${color}30`,
              }}>
                {label}
              </span>
            ))}
          </div>
        }
      >
        {/* inline alerts — inside the card, above the grid */}
        {(pendingDocs.length > 0 || rejectedDocs.length > 0 || expiringSoon.length > 0) && (
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 12 }}>
            {pendingDocs.length > 0 && (
              <AlertStrip color="var(--wd-orange)" icon="⚠️">
                <strong>{pendingDocs.length} pending</strong>
                {" — "}{pendingDocs.map(d => humanise(d.type)).join(", ")}
              </AlertStrip>
            )}
            {rejectedDocs.length > 0 && (
              <AlertStrip color="var(--wd-red)" icon="❌">
                <strong>{rejectedDocs.length} rejected</strong>
                {" — "}{rejectedDocs.map(d => humanise(d.type)).join(", ")}
              </AlertStrip>
            )}
            {expiringSoon.length > 0 && (
              <AlertStrip color="var(--wd-orange)" icon="⏰">
                <strong>{expiringSoon.length} expiring soon</strong>
                {" — "}{expiringSoon.map(d => humanise(d.type)).join(", ")}
              </AlertStrip>
            )}
          </div>
        )}

        {docs.length === 0 ? (
          <div style={{
            padding: "28px 0", textAlign: "center",
            color: "var(--wd-t3)", fontSize: 13, fontStyle: "italic",
          }}>
            No documents uploaded yet
          </div>
        ) : (
          /* strict 4 columns — equal width, never more, never less */
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
          }}>
            {docs.map(doc => (
              <DocCard key={doc.id} doc={doc} />
            ))}
          </div>
        )}
      </SectionCard>

       {/* ── row 4: surveyor + payment ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
 
        <SectionCard title="Surveyor details" icon="🔍">
          {!caseInfo.surveyorName ? (
            <div style={{ fontSize: 12, color: "var(--wd-t3)", fontStyle: "italic", padding: "4px 0" }}>
              No surveyor assigned yet.
            </div>
          ) : (
            <FieldGrid fields={[
              { label: "Surveyor",       value: caseInfo.surveyorName },
              { label: "Phone",          value: caseInfo.surveyorPhone,        mono: true },
              { label: "Email",          value: caseInfo.surveyorEmail },
              { label: "Assigned on",    value: formatDate(caseInfo.surveyorAssignedAt) },
              { label: "Survey type",    value: caseInfo.surveyType },
              {
                label: "Survey status",
                value: caseInfo.surveyStatus || "Pending",
                badge: {
                  color: caseInfo.surveyStatus === "COMPLETED"
                    ? "var(--wd-green)"
                    : caseInfo.surveyStatus === "ACCEPTED"
                    ? "var(--wd-blue)"
                    : "var(--wd-orange)",
                },
              },
            ]} />
          )}
        </SectionCard>
 
        <SectionCard title="Payment snapshot" icon="💰">
          <FieldGrid fields={[
            {
              label: "Workshop estimate",
              value: caseInfo.workshopEstimateAmount
                ? formatINR(caseInfo.workshopEstimateAmount)
                : null,
            },
            {
              label: "Insurance approved",
              value: caseInfo.approvedAmount
                ? formatINR(caseInfo.approvedAmount)
                : null,
            },
            {
              label: "Customer due",
              value: caseInfo.customerDue
                ? formatINR(caseInfo.customerDue)
                : null,
            },
            {
              label: "Customer paid",
              value: caseInfo.customerPaymentStatus || "Not paid",
              badge: {
                color: caseInfo.customerPaymentStatus === "PAID"
                  ? "var(--wd-green)"
                  : "var(--wd-t3)",
              },
            },
            {
              label: "Workshop payable",
              value: caseInfo.workshopPayable
                ? formatINR(caseInfo.workshopPayable)
                : null,
            },
            {
              label: "Workshop paid",
              value: caseInfo.workshopPaymentStatus || "Not paid",
              badge: {
                color: caseInfo.workshopPaymentStatus === "PAID"
                  ? "var(--wd-green)"
                  : "var(--wd-t3)",
              },
            },
          ]} />
        </SectionCard>
      </div>

    </div>
  );
}